// VITE_API_URL wird beim Build gesetzt (z.B. https://edge-marketing.leftclick.ch/api)
// Fallback auf lokalen Proxy für Dev-Umgebung
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) || '/api';

class ApiClient {
  private accessToken: string | null = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
  private readonly defaultTimeoutMs = 12000;
  private refreshPromise: Promise<boolean> | null = null;
  private sessionVersion = 0;

  private navigateToLogin() {
    // App uses hash-based routing, so hard redirects must keep the hash format.
    window.location.hash = '/auth/login';
  }

  setToken(token: string | null) {
    if (this.accessToken === token) return;
    this.accessToken = token;
    this.sessionVersion += 1;
    this.refreshPromise = null;
  }

  getToken(): string | null {
    return this.accessToken;
  }

  private async parseResponse<T>(response: Response, allowAuthError = false): Promise<T> {
    const rawBody = await response.text();
    let data: unknown = {};
    try {
      if (rawBody) data = JSON.parse(rawBody);
    } catch {
      const statusInfo = `${response.status} ${response.statusText}`.trim();
      throw new Error(`Unerwartete API-Antwort (${statusInfo}).`);
    }
    if (!response.ok && !allowAuthError) {
      const error = (data as { error?: unknown } | null)?.error;
      throw new Error(typeof error === 'string' ? error : `Anfrage fehlgeschlagen (${response.status}).`);
    }
    return data as T;
  }

  private async request<T>(
    path: string,
    options: RequestInit & { timeoutMs?: number } = {},
    readBody?: (response: Response) => Promise<T>,
  ): Promise<T> {
    const { timeoutMs, ...fetchInit } = options;
    const headers = new Headers(fetchInit.headers);
    const sessionVersion = this.sessionVersion;
    const requestToken = this.accessToken;
    const assertCurrentSession = () => {
      if (sessionVersion !== this.sessionVersion) throw new Error('Die Sitzung wurde geändert. Bitte erneut versuchen.');
    };

    if (this.accessToken) {
      headers.set('Authorization', `Bearer ${this.accessToken}`);
    }

    if (!(fetchInit.body instanceof FormData)) {
      headers.set('Content-Type', 'application/json');
    }

    const ms = timeoutMs ?? this.defaultTimeoutMs;
    const controller = new AbortController();
    let timedOut = false;
    const timeout = setTimeout(() => { timedOut = true; controller.abort(); }, ms);
    const abort = () => controller.abort();
    if (fetchInit.signal?.aborted) abort();
    else fetchInit.signal?.addEventListener('abort', abort, { once: true });
    const authPath = path.split('?')[0] ?? path;
    const isAuthAction = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout'].includes(authPath);

    try {
      let response = await fetch(`${API_BASE}${path}`, {
        ...fetchInit,
        headers,
        signal: controller.signal,
      });
      assertCurrentSession();
      if (response.status === 401 && !isAuthAction && !path.startsWith('/public/')) {
        // Parallel requests may have failed with the same expired access token.
        // Reuse the renewed token, or share the one in-flight refresh.
        const refreshed = requestToken !== this.accessToken && this.accessToken
          ? true
          : await this.waitForRefresh(controller.signal);
        controller.signal.throwIfAborted();
        assertCurrentSession();
        if (refreshed) {
          headers.set('Authorization', `Bearer ${this.accessToken}`);
          response = await fetch(`${API_BASE}${path}`, { ...fetchInit, headers, signal: controller.signal });
          assertCurrentSession();
        }
        if (!refreshed || response.status === 401) {
          this.setToken(null);
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          this.navigateToLogin();
          throw new Error('Die Sitzung ist abgelaufen. Bitte erneut anmelden.');
        }
      }
      if (readBody) {
        if (!response.ok) await this.parseResponse(response);
        const body = await readBody(response);
        assertCurrentSession();
        return body;
      }
      const body = await this.parseResponse<T>(response, isAuthAction);
      assertCurrentSession();
      return body;
    } catch (error) {
      if (timedOut) {
        throw new Error('API Timeout. Bitte erneut versuchen.');
      }
      throw error;
    } finally {
      clearTimeout(timeout);
      fetchInit.signal?.removeEventListener('abort', abort);
    }
  }

  private waitForRefresh(signal: AbortSignal): Promise<boolean> {
    signal.throwIfAborted();
    return new Promise((resolve, reject) => {
      const onAbort = () => {
        signal.removeEventListener('abort', onAbort);
        reject(signal.reason);
      };
      signal.addEventListener('abort', onAbort, { once: true });
      // Aborting one caller must not cancel refresh for the other requests.
      this.refreshToken().then(
        (refreshed) => { signal.removeEventListener('abort', onAbort); resolve(refreshed); },
        (error: unknown) => { signal.removeEventListener('abort', onAbort); reject(error); },
      );
    });
  }

  private refreshToken(): Promise<boolean> {
    if (this.refreshPromise) return this.refreshPromise;
    const pending = this.performRefresh();
    this.refreshPromise = pending;
    void pending.finally(() => { if (this.refreshPromise === pending) this.refreshPromise = null; });
    return pending;
  }

  private async performRefresh(): Promise<boolean> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;
    const sessionVersion = this.sessionVersion;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), this.defaultTimeoutMs);
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
        signal: controller.signal,
      });
      const data = await res.json();
      if (res.ok && data.success && typeof data.data?.access_token === 'string' && sessionVersion === this.sessionVersion) {
        this.accessToken = data.data.access_token;
        localStorage.setItem('access_token', data.data.access_token);
        if (data.data.refresh_token) {
          localStorage.setItem('refresh_token', data.data.refresh_token);
        }
        return true;
      }
    } catch {
      return false;
    } finally {
      clearTimeout(timeout);
    }
    return false;
  }

  get<T>(path: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(path, options);
  }

  post<T>(path: string, body?: unknown, opts?: { timeoutMs?: number }): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      timeoutMs: opts?.timeoutMs,
    });
  }

  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' });
  }

  upload<T>(path: string, formData: FormData, opts?: { timeoutMs?: number }): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body: formData,
      timeoutMs: opts?.timeoutMs ?? 120000,
    });
  }

  async getBlob(path: string): Promise<Blob> {
    return this.request(path, {}, (response) => response.blob());
  }
}

export const api = new ApiClient();

export const automationApi = {
  listWorkflows: <T = unknown>() => api.get<T>('/automation/workflows'),
  createWorkflow: <T = unknown>(payload: Record<string, unknown>) => api.post<T>('/automation/workflows', payload),
  updateWorkflow: <T = unknown>(id: string, payload: Record<string, unknown>) => api.patch<T>(`/automation/workflows/${id}`, payload),
  toggleWorkflow: <T = unknown>(id: string) => api.post<T>(`/automation/workflows/${id}/toggle`),
  executeWorkflow: <T = unknown>(id: string, payload: Record<string, unknown>) => api.post<T>(`/automation/workflows/${id}/execute`, payload),
  listRuns: <T = unknown>(query = '') => api.get<T>(`/automation/runs${query ? `?${query}` : ''}`),
  getRun: <T = unknown>(id: string) => api.get<T>(`/automation/runs/${id}`),
  retryRun: <T = unknown>(id: string, payload?: { approved?: boolean }) => api.post<T>(`/automation/runs/${id}/retry`, payload ?? {}),
  dashboard: <T = unknown>() => api.get<T>('/automation/dashboard'),
  preview: <T = unknown>(payload: Record<string, unknown>) => api.post<T>('/automation/preview', payload),
};
